import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  Globe2,
  MessageCircle,
  Play,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Zap,
  Shield,
  Layers,
  Cpu,
  BarChart3,
  Calendar,
} from "lucide-react";
import Footer from "../../components/footer/Footer";

/* ─────────────────────────────────────────────
   SHARED PRIMITIVES
───────────────────────────────────────────── */
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut", delay },
});

const Chip = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-black shadow-sm">
    {children}
  </span>
);

const GoldDot = () => (
  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#FFD221]" />
);

function Hero() {
  return (
    <section className="overflow-hidden relative px-6 pt-32 pb-24 text-black bg-white lg:px-12">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-[-10%] h-[560px] w-[560px] rounded-full bg-[#FFD221]/20 blur-[120px]" />
        <div className="absolute right-[-8%] top-20 h-[420px] w-[420px] rounded-full bg-[#f7f4ec] blur-[80px]" />
        <div
          className="absolute inset-0 opacity-[0.5]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(0,0,0,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,.04) 1px,transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl">
        <div className="grid gap-14 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Chip>
                <Sparkles size={13} />
                About InfoBoxx 5.0
              </Chip>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.08 }}
              className="mt-7 max-w-3xl text-5xl font-black leading-[1.02] tracking-tight text-black sm:text-6xl lg:text-[4.7rem]"
            >
              From questions to{" "}
              <span className="inline-block relative whitespace-nowrap">
                <span
                  style={{
                    background:
                      "linear-gradient(135deg,#c9a000 0%,#FFD221 58%,#a88400 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  clear decisions
                </span>
                <svg
                  className="absolute left-0 -bottom-3 w-full h-3"
                  viewBox="0 0 240 12"
                  fill="none"
                  preserveAspectRatio="none"
                  aria-hidden
                >
                  <path
                    d="M3 8C61 1.5 168 1.5 237 8"
                    stroke="#FFD221"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.16 }}
              className="mt-7 max-w-xl text-lg leading-relaxed text-gray-600"
            >
              InfoBoxx is an intent-led platform that helps you understand first
              — then take action with confidence through trusted content,
              verified providers, and guided next steps.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.24 }}
              className="flex flex-wrap gap-3 mt-9"
            >
              <Link
                to="/#categories"
                className="inline-flex items-center gap-2 rounded-2xl bg-[#FFD221] px-7 py-4 text-sm font-black text-black shadow-[0_18px_40px_-22px_rgba(0,0,0,0.55)] transition-colors hover:bg-[#e6bc00]"
              >
                Explore Categories <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex gap-2 items-center px-7 py-4 text-sm font-black text-black bg-white rounded-2xl border shadow-sm transition-all border-black/10 hover:border-black/25 hover:shadow-md"
              >
                Ask a Question
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.32 }}
              className="grid grid-cols-3 gap-3 mt-10 max-w-xl"
            >
              {[
                { v: "SMS", l: "to platform evolution" },
                { v: "6", l: "category mini-sites" },
                { v: "60+", l: "decision topics" },
              ].map(({ v, l }) => (
                <div
                  key={l}
                  className="rounded-2xl border border-black/[0.07] bg-[#f7f4ec] p-4"
                >
                  <p className="text-2xl font-black text-black">{v}</p>
                  <p className="mt-1 text-[11px] leading-snug text-gray-500">
                    {l}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="relative"
          >
            <div className="absolute -left-6 top-12 hidden h-32 w-32 rounded-[2rem] bg-[#FFD221] lg:block" />
            <div className="absolute -right-5 bottom-8 hidden h-36 w-36 rounded-full border-[22px] border-[#f7f4ec] lg:block" />

            <div className="relative overflow-hidden rounded-[2.4rem] border border-black/[0.08] bg-white p-3 shadow-[0_35px_90px_-45px_rgba(0,0,0,0.55)]">
              <div className="relative overflow-hidden rounded-[1.8rem] bg-gray-950">
                <img
                  src="https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1200&q=85"
                  alt="Team using digital tools to make informed decisions"
                  className="h-[520px] w-full object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/70 via-black/10" />

                <div className="absolute top-6 left-6 px-4 py-2 text-xs font-black text-black rounded-full border backdrop-blur border-white/20 bg-white/90">
                  Intent-led guidance
                </div>

                <div className="absolute right-6 bottom-6 left-6 p-5 text-black rounded-3xl border shadow-2xl backdrop-blur border-white/15 bg-white/95">
                  <div className="flex gap-4 justify-between items-center">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#c9a000]">
                        User journey
                      </p>
                      <p className="mt-1 text-xl font-black leading-tight">
                        Question → clarity → verified action
                      </p>
                    </div>
                    <div className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#FFD221] sm:flex">
                      <ArrowRight size={22} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute bottom-24 left-3 hidden max-w-[220px] rounded-3xl border border-black/[0.08] bg-white p-4 shadow-2xl md:block lg:bottom-40 lg:-left-6"
            >
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FFD221]">
                <MessageCircle size={18} />
              </div>
              <p className="text-sm font-black text-black">Ask first</p>
              <p className="mt-1 text-xs leading-relaxed text-gray-500">
                Users start with real questions, not random ads.
              </p>
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
              className="absolute -right-2 top-20 hidden max-w-[230px] rounded-3xl border border-black/[0.08] bg-gray-950 p-4 text-white shadow-2xl md:block lg:-right-10"
            >
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FFD221] text-black">
                <Shield size={18} />
              </div>
              <p className="text-sm font-black">Verified providers</p>
              <p className="mt-1 text-xs leading-relaxed text-gray-400">
                Experts receive warmer, better-informed prospects.
              </p>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mt-16 rounded-[2rem] border border-black/[0.07] bg-[#f7f4ec] p-4 shadow-sm"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-gray-500">
              Built across decision categories
            </p>
            <div className="flex flex-wrap gap-2">
              {["Legal", "Health", "Real Estate", "Careers", "Nutrition", "Betting & Gaming"].map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-black/10 bg-white px-4 py-1.5 text-xs font-black text-gray-700"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   2. STORY / INTRODUCTION
───────────────────────────────────────────── */
function Story() {
  const timeline = [
    {
      label: "The Origin",
      year: "Day 1",
      title: "InfoServices — SMS Roots",
      text: "We began as InfoServices, an SMS-based solution delivering helpful information directly to people's phones — fast, clear, and on demand.",
    },
    {
      label: "The Insight",
      year: "Pivot",
      title: "A deeper observation",
      text: "People don't just want information — they want clarity before making decisions. They search, ask questions, compare, and only then decide.",
    },
    {
      label: "The Evolution",
      year: "Now",
      title: "InfoBoxx 5.0",
      text: "An intent-led advertorial content platform that captures curiosity early, educates clearly, builds trust naturally, and converts intent into real action.",
    },
  ];

  return (
    <section className="bg-[#f7f4ec] px-6 py-24 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
          {/* left copy */}
          <div>
            <motion.div {...fadeUp(0)}>
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
                Our Journey
              </p>
              <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
                From InfoServices <br />
                to InfoBoxx
              </h2>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-gray-600">
                InfoBoxx did not start as a platform — it started as a simple
                idea. We solved a powerful problem: people needed access to
                information quickly, clearly, and on demand.
              </p>
            </motion.div>
          </div>

          {/* right timeline */}
          <div className="relative pl-6">
            {/* vertical bar */}
            <div className="absolute left-0 top-2 bottom-2 w-px bg-gradient-to-b from-[#FFD221] via-[#c9a000] to-transparent" />

            <div className="space-y-10">
              {timeline.map(({ label, year, title, text }, i) => (
                <motion.div key={label} {...fadeUp(i * 0.12)} className="relative">
                  {/* dot */}
                  <span className="absolute -left-[21px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-[#FFD221] bg-[#f7f4ec]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#FFD221]" />
                  </span>

                  <span className="inline-block rounded-full bg-[#FFD221]/20 px-3 py-0.5 text-[10px] font-black uppercase tracking-widest text-[#c9a000]">
                    {year}
                  </span>
                  <h3 className="mt-2 text-lg font-black text-black">{title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{text}</p>
                </motion.div>
              ))}
            </div>

            {/* destination callout */}
            <motion.div
              {...fadeUp(0.4)}
              className="mt-10 rounded-2xl border-l-4 border-[#FFD221] bg-gray-950 px-6 py-5 text-white"
            >
              <p className="text-xs font-black uppercase tracking-widest text-[#FFD221]">
                Destination
              </p>
              <p className="mt-2 text-base font-bold leading-snug">
                InfoBoxx 5.0 — an intent-led advertorial content platform
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   3. WHO WE ARE
───────────────────────────────────────────── */
function WhoWeAre() {
  const pillars = [
    { icon: Layers, label: "Information systems" },
    { icon: Globe2, label: "Digital engagement platforms" },
    { icon: BarChart3, label: "Data-driven user journeys" },
    { icon: TrendingUp, label: "Conversion-focused ecosystems" },
  ];

  return (
    <section className="px-6 py-24 bg-white lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:items-center">
          {/* left visual */}
          <motion.div {...fadeUp(0)} className="relative">
            <div className="overflow-hidden rounded-[2.5rem] bg-gray-950 p-8 shadow-2xl">
              <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-[#FFD221]/10 blur-3xl" />
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#FFD221]">
                Powered by
              </p>
              <h3 className="mt-3 text-2xl font-black leading-tight text-white">
                GWO SEVO Company Limited
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-gray-400">
                A technology-driven company focused on building digital platforms
                that solve real-world problems across African markets.
              </p>
              <div className="grid grid-cols-2 gap-3 mt-8">
                {pillars.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex gap-3 items-center px-4 py-3 rounded-2xl border border-white/10 bg-white/5"
                  >
                    <Icon size={16} className="shrink-0 text-[#FFD221]" />
                    <span className="text-xs font-semibold leading-snug text-gray-200">
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* right copy */}
          <div>
            <motion.div {...fadeUp(0)}>
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
                Who We Are
              </p>
              <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
                Bridging questions <br className="hidden sm:block" />
                to answers
              </h2>
              <p className="mt-5 text-base leading-relaxed text-gray-600">
                InfoBoxx is powered by GWO SEVO Company Limited, a
                technology-driven company focused on building digital platforms
                that solve real-world problems across African markets.
              </p>
            </motion.div>

            <motion.div
              {...fadeUp(0.14)}
              className="mt-8 rounded-3xl border border-[#FFD221]/30 bg-[#f7f4ec] p-6"
            >
              <p className="text-sm font-black text-black">Our simple goal</p>
              <p className="mt-2 text-base leading-relaxed text-gray-700">
                To bridge the gap between people searching for answers and
                experts who can help them.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   4. WHAT WE DO — funnel
───────────────────────────────────────────── */
function WhatWeDo() {
  const steps = [
    { n: "01", label: "You start with a question" },
    { n: "02", label: "You learn through trusted content" },
    { n: "03", label: "You connect with a verified expert" },
    { n: "04", label: "You take action confidently" },
  ];

  return (
    <section className="bg-[#f7f4ec] px-6 py-24 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="mb-14 max-w-2xl">
          <motion.div {...fadeUp(0)}>
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
              What We Do
            </p>
            <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
              We sit between your question and your decision
            </h2>
          </motion.div>
        </div>

        <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
          {/* steps */}
          <div className="space-y-4">
            {steps.map(({ n, label }, i) => (
              <motion.div
                key={n}
                {...fadeUp(i * 0.1)}
                className="flex gap-5 items-center px-6 py-5 bg-white rounded-2xl border shadow-sm border-black/8"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFD221] text-sm font-black text-black">
                  {n}
                </span>
                <span className="font-semibold text-gray-800">{label}</span>
              </motion.div>
            ))}
          </div>

          {/* principle card */}
          <motion.div
            {...fadeUp(0.2)}
            className="p-8 text-white rounded-3xl shadow-2xl bg-gray-950"
          >
            <p className="text-[11px] font-black uppercase tracking-widest text-[#FFD221]">
              Core principle
            </p>
            <div className="mt-6 space-y-6">
              {[
                { step: "Educate first", icon: BookOpen },
                { step: "Build trust second", icon: Shield },
                { step: "Convert third", icon: TrendingUp },
              ].map(({ step, icon: Icon }, i) => (
                <div key={step} className="flex gap-4 items-center">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#FFD221]/30 bg-[#FFD221]/10">
                    <Icon size={18} className="text-[#FFD221]" />
                  </div>
                  <p className="text-lg font-black">{step}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 rounded-2xl border border-[#FFD221]/30 bg-[#FFD221]/8 px-5 py-4">
              <p className="text-sm leading-relaxed text-gray-300">
                Instead of showing you ads too early, we guide you through a
                structured journey — from curiosity to confident action.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   5. OBJECTIVES — bento grid
───────────────────────────────────────────── */
function Objectives() {
  const primary = [
    "Capture high-intent user signals early",
    "Convert curiosity into qualified leads",
    "Monetize information in a meaningful way",
    "Build trust between users and service providers",
  ];
  const secondary = [
    "Create category-specific authority platforms",
    "Leverage first-party data for smarter engagement",
    "Maximize SMS and digital traffic value",
    "Reduce customer acquisition costs for partners",
  ];

  return (
    <section className="px-6 py-24 bg-white lg:px-12">
      <div className="mx-auto max-w-7xl">
        <motion.div {...fadeUp(0)} className="mb-14 max-w-2xl">
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
            Our Objectives
          </p>
          <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
            What we're building toward
          </h2>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* primary */}
          <motion.div
            {...fadeUp(0.08)}
            className="rounded-3xl border border-[#FFD221]/30 bg-[#f7f4ec] p-8"
          >
            <div className="flex gap-3 items-center mb-6">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#FFD221]">
                <Target size={18} className="text-black" />
              </span>
              <p className="text-sm font-black tracking-widest text-black uppercase">
                Primary
              </p>
            </div>
            <ul className="space-y-4">
              {primary.map((p) => (
                <li key={p} className="flex gap-3 items-start">
                  <CheckCircle2
                    size={17}
                    className="mt-0.5 shrink-0 text-[#c9a000]"
                  />
                  <span className="text-sm leading-relaxed text-gray-700">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* secondary */}
          <motion.div
            {...fadeUp(0.16)}
            className="p-8 bg-white rounded-3xl border shadow-sm border-black/8"
          >
            <div className="flex gap-3 items-center mb-6">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-black/10 bg-[#f7f4ec]">
                <Layers size={18} className="text-black" />
              </span>
              <p className="text-sm font-black tracking-widest text-black uppercase">
                Secondary
              </p>
            </div>
            <ul className="space-y-4">
              {secondary.map((p) => (
                <li key={p} className="flex gap-3 items-start">
                  <GoldDot />
                  <span className="text-sm leading-relaxed text-gray-700">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   6. FEATURES — icon grid
───────────────────────────────────────────── */
function Features() {
  const items = [
    {
      icon: Target,
      title: "Intent Capture Engine",
      text: "We capture real questions through SMS, Search, Social media, and Ads.",
      accent: true,
    },
    {
      icon: BookOpen,
      title: "Structured Content Funnel",
      text: "Hook → Context → Education → Authority → Action. Every experience follows this proven flow.",
    },
    {
      icon: Layers,
      title: "Multi-Category Platform",
      text: "Each category operates as its own focused mini-site with dedicated expert content.",
    },
    {
      icon: Cpu,
      title: "AI-Powered Assistance",
      text: "Users can ask questions and get instant guidance before connecting with an expert.",
    },
    {
      icon: Zap,
      title: "Conversion Engine",
      text: "Users can fill forms, chat via WhatsApp, call, or book appointments — all in one journey.",
    },
    {
      icon: BarChart3,
      title: "First-Party Data System",
      text: "We track behavior to improve user experience and optimize outcomes for partners.",
    },
  ];

  return (
    <section className="bg-[#f7f4ec] px-6 py-24 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <motion.div {...fadeUp(0)} className="mb-14 max-w-2xl">
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
            What Makes Us Different
          </p>
          <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
            Built different. Built for intent.
          </h2>
        </motion.div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map(({ icon: Icon, title, text, accent }, i) => (
            <motion.div
              key={title}
              {...fadeUp(i * 0.08)}
              whileHover={{ y: -5 }}
              className={`rounded-3xl border p-7 transition-shadow hover:shadow-xl ${
                accent
                  ? "border-[#FFD221]/40 bg-gray-950 text-white"
                  : "border-black/8 bg-white"
              }`}
            >
              <div
                className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl ${
                  accent ? "bg-[#FFD221]" : "bg-[#FFD221]/15"
                }`}
              >
                <Icon size={22} className={accent ? "text-black" : "text-[#c9a000]"} />
              </div>
              <h3
                className={`text-base font-black ${
                  accent ? "text-white" : "text-black"
                }`}
              >
                {title}
              </h3>
              <p
                className={`mt-3 text-sm leading-relaxed ${
                  accent ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   7. WHY INFOBOXX
───────────────────────────────────────────── */
function WhyInfoBoxx() {
  const problems = [
    "People don't trust ads easily",
    "Information is everywhere but clarity is rare",
    "Decisions are becoming more complex",
  ];
  const solutions = [
    "Giving users clarity before decisions",
    "Helping businesses reach customers earlier",
    "Turning information into action",
  ];

  return (
    <section className="px-6 py-24 text-white bg-gray-950 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <motion.div {...fadeUp(0)} className="mb-16 text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#FFD221]">
            Why InfoBoxx Exists
          </p>
          <h2 className="mt-3 text-4xl font-black leading-tight sm:text-5xl">
            A platform built on trust and intent
          </h2>
        </motion.div>

        <div className="grid gap-8 lg:grid-cols-2">
          {/* problems */}
          <motion.div
            {...fadeUp(0.1)}
            className="p-8 rounded-3xl border backdrop-blur border-white/10 bg-white/5"
          >
            <p className="mb-6 text-xs font-black tracking-widest text-gray-400 uppercase">
              In today's world
            </p>
            <ul className="space-y-4">
              {problems.map((p) => (
                <li key={p} className="flex gap-3 items-start">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-500" />
                  <span className="text-sm leading-relaxed text-gray-300">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* solutions */}
          <motion.div
            {...fadeUp(0.18)}
            className="rounded-3xl border border-[#FFD221]/30 bg-[#FFD221]/8 p-8"
          >
            <p className="mb-6 text-xs font-black uppercase tracking-widest text-[#FFD221]">
              InfoBoxx solves this by
            </p>
            <ul className="space-y-4">
              {solutions.map((s) => (
                <li key={s} className="flex gap-3 items-start">
                  <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-[#FFD221]" />
                  <span className="text-sm leading-relaxed text-gray-200">{s}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 rounded-2xl border border-[#FFD221]/30 bg-[#FFD221]/10 px-5 py-4">
              <p className="text-sm font-bold leading-relaxed text-white">
                InfoBoxx is not just a content platform — it is a{" "}
                <span className="text-[#FFD221]">conversion infrastructure</span>{" "}
                built on trust and intent.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   8. CATEGORIES
───────────────────────────────────────────── */
function Categories() {
  const cats = [
    { emoji: "⚖️", name: "Legal", href: "/legal", desc: "Land, marriage, criminal law & more" },
    { emoji: "🏥", name: "Health", href: "/health", desc: "Medical guidance & wellness journeys" },
    { emoji: "🏠", name: "Real Estate", href: "/real-estate", desc: "Property, mortgages & investment" },
    { emoji: "💼", name: "Careers", href: "/careers", desc: "Jobs, CVs & career transitions" },
    { emoji: "🎮", name: "Betting & Gaming", href: "/betting-gaming", desc: "Responsible play & tips" },
    { emoji: "🥗", name: "Nutrition & Meal Plans", href: "/nutrition", desc: "Diet, wellness & meal guidance" },
  ];

  return (
    <section className="bg-[#f7f4ec] px-6 py-24 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <motion.div {...fadeUp(0)} className="mb-14">
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
            Our Categories
          </p>
          <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
            Six mini-platforms. <br className="hidden sm:block" />
            One trusted ecosystem.
          </h2>
          <p className="mt-4 max-w-xl text-base text-gray-600">
            Each category operates as a dedicated mini-platform with expert-backed
            content and verified services.
          </p>
        </motion.div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cats.map(({ emoji, name, href, desc }, i) => (
            <motion.div key={name} {...fadeUp(i * 0.08)}>
              <Link
                to={href}
                className="group flex items-center gap-5 rounded-3xl border border-black/8 bg-white px-6 py-5 shadow-sm transition-all hover:-translate-y-1 hover:border-[#FFD221]/60 hover:shadow-lg"
              >
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#f7f4ec] text-2xl group-hover:bg-[#FFD221] transition-colors">
                  {emoji}
                </span>
                <div className="min-w-0">
                  <p className="font-black text-black">{name}</p>
                  <p className="mt-0.5 text-xs leading-snug text-gray-500">{desc}</p>
                </div>
                <ArrowRight
                  size={16}
                  className="ml-auto text-gray-300 transition shrink-0 group-hover:text-black"
                />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   9. PROMISE
───────────────────────────────────────────── */
function Promise() {
  const promises = [
    "We will always educate before we sell",
    "We will connect you only with verified providers",
    "We will keep information clear, simple, and relevant",
    "We will respect your data and privacy",
    "We will help you make confident decisions",
  ];

  return (
    <section className="px-6 py-24 bg-white lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <motion.div {...fadeUp(0)}>
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
              Our Promise to You
            </p>
            <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
              Commitments we keep, every day
            </h2>
            <p className="mt-5 text-base leading-relaxed text-gray-600">
              These are not marketing lines. These are the principles that
              guide every product decision we make.
            </p>
          </motion.div>

          <div className="space-y-4">
            {promises.map((p, i) => (
              <motion.div
                key={p}
                {...fadeUp(i * 0.08)}
                className="flex items-center gap-4 rounded-2xl border border-black/8 bg-[#f7f4ec] px-5 py-4"
              >
                <CheckCircle2 size={20} className="shrink-0 text-[#c9a000]" />
                <span className="text-sm font-semibold leading-relaxed text-gray-800">
                  {p}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   10. BECOME A PARTNER
───────────────────────────────────────────── */
function BecomePartnerSection() {
  const steps = [
    { n: "01", title: "Register Your Company", desc: "Enter company and contact details" },
    { n: "02", title: "Submit Verification Documents", desc: "Certificate of Incorporation, TIN & Business Registration" },
    { n: "03", title: "Select Category & Services", desc: "Choose your area: Legal, Health, Real Estate, and more" },
    { n: "04", title: "Set Up Your Profile & Content", desc: "Add services, content, and how you handle leads" },
    { n: "05", title: "Get Approved & Start Receiving Leads", desc: "Once approved you start receiving qualified leads" },
  ];

  return (
    <section className="px-6 py-24 text-white bg-gray-950 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-start">
          {/* left */}
          <div>
            <motion.div {...fadeUp(0)}>
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#FFD221]">
                Become a Partner
              </p>
              <h2 className="mt-3 text-4xl font-black leading-tight sm:text-5xl">
                Reach people already looking for your services
              </h2>
              <p className="mt-5 text-base leading-relaxed text-gray-400">
                InfoBoxx connects you with people who are already looking for
                your services — not random traffic. Our onboarding ensures only
                verified providers are listed and matched with the right users.
              </p>
            </motion.div>

            <motion.div {...fadeUp(0.2)} className="mt-10">
              <Link
                to="/partners#how-to-become-a-partner"
                className="inline-flex items-center gap-2 rounded-xl bg-[#FFD221] px-7 py-3.5 text-sm font-black text-black shadow-lg hover:bg-[#e6bc00] transition-colors"
              >
                Apply to Become a Partner <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>

          {/* right — steps */}
          <div className="relative pl-8">
            <div className="absolute left-0 top-2 bottom-2 w-px bg-gradient-to-b from-[#FFD221] to-transparent" />

            <div className="space-y-8">
              {steps.map(({ n, title, desc }, i) => (
                <motion.div key={n} {...fadeUp(i * 0.1)} className="relative">
                  <span className="absolute -left-[2.15rem] top-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#FFD221] text-[10px] font-black text-black">
                    {n.replace("0", "")}
                  </span>
                  <h3 className="text-base font-black text-white">{title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-gray-400">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   11. DEMO / PLATFORM WALKTHROUGH
───────────────────────────────────────────── */
function Demo() {
  const [playing, setPlaying] = useState(false);

  return (
    <section className="bg-[#f7f4ec] px-6 py-24 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <motion.div {...fadeUp(0)} className="mb-12 text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
            Platform Walkthrough
          </p>
          <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
            See How InfoBoxx Works
          </h2>
        </motion.div>

        {/* video placeholder */}
        <motion.div {...fadeUp(0.12)} className="relative mx-auto max-w-4xl">
          <div
            className="relative overflow-hidden rounded-[2rem] bg-gray-950 shadow-2xl"
            style={{ aspectRatio: "16/9" }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,210,33,0.1),transparent_60%)]" />
            <div className="flex absolute inset-0 flex-col gap-6 justify-center items-center">
              <motion.button
                onClick={() => setPlaying(!playing)}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                className="flex h-20 w-20 items-center justify-center rounded-full bg-[#FFD221] shadow-2xl shadow-[#FFD221]/40"
                aria-label="Play explainer video"
              >
                <Play size={28} className="ml-1 text-black" fill="black" />
              </motion.button>
              <p className="text-sm font-semibold text-gray-400">
                [Explainer Video — Platform Walkthrough]
              </p>
            </div>
          </div>
        </motion.div>

        {/* quick actions */}
        <motion.div
          {...fadeUp(0.2)}
          className="flex flex-wrap gap-4 justify-center mt-10"
        >
          {[
            { icon: MessageCircle, label: "Ask a Question", href: "/contact" },
            { icon: Users, label: "Talk to an Expert", href: "/contact" },
            { icon: Calendar, label: "Book an Appointment", href: "/contact#contact-book-appointment" },
          ].map(({ icon: Icon, label, href }) => (
            <Link
              key={label}
              to={href}
              className="inline-flex gap-2 items-center px-6 py-3 text-sm font-black text-black bg-white rounded-xl border shadow-sm transition-all border-black/10 hover:border-black/30 hover:shadow-md"
            >
              <Icon size={16} className="text-[#c9a000]" />
              {label}
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   12. PARTNERS STRIP
───────────────────────────────────────────── */
function PartnersStrip() {
  return (
    <section className="px-6 py-24 bg-white lg:px-12">
      <div className="mx-auto max-w-7xl">
        <motion.div {...fadeUp(0)} className="text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
            Our Trusted Partners
          </p>
          <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
            Powered by verified experts
          </h2>
          <p className="mt-4 text-base text-gray-600">
            Each category is powered by expert service providers.
          </p>
        </motion.div>

        <motion.div
          {...fadeUp(0.12)}
          className="grid gap-5 mt-12 sm:grid-cols-2 lg:grid-cols-3"
        >
          {[
            { name: "Afrimore", cat: "Legal", emoji: "⚖️", href: "/legal", featured: true },
            { name: "Partner Slot", cat: "Health", emoji: "🏥", href: "/partners#how-to-become-a-partner" },
            { name: "Partner Slot", cat: "Real Estate", emoji: "🏠", href: "/partners#how-to-become-a-partner" },
          ].map(({ name, cat, emoji, href, featured }) => (
            <Link
              key={`${name}-${cat}`}
              to={href}
              className={`group flex items-center gap-5 rounded-3xl border p-6 transition-all hover:-translate-y-1 hover:shadow-lg ${
                featured
                  ? "border-[#FFD221]/50 bg-gray-950 text-white"
                  : "border-black/8 bg-[#f7f4ec] text-black"
              }`}
            >
              <span className="flex justify-center items-center w-14 h-14 text-2xl bg-white rounded-2xl shadow-sm shrink-0">
                {emoji}
              </span>
              <div>
                <p className={`font-black ${featured ? "text-white" : "text-black"}`}>
                  {name}
                </p>
                <p className={`text-xs ${featured ? "text-gray-400" : "text-gray-500"}`}>
                  {cat} Partner
                </p>
                {featured && (
                  <span className="mt-2 inline-block rounded-full bg-[#FFD221]/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-[#FFD221]">
                    Featured
                  </span>
                )}
              </div>
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   13. TESTIMONIALS
───────────────────────────────────────────── */
function Testimonials() {
  const items = [
    {
      type: "User",
      quote:
        "InfoBoxx helped me understand land issues before speaking to a lawyer. I felt confident and prepared.",
      avatar: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=80&h=80&fit=crop&q=80",
      name: "Abena M.",
      role: "InfoBoxx User",
    },
    {
      type: "Partner",
      quote:
        "We receive clients who already understand their needs. The quality of leads is significantly higher.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&q=80",
      name: "Kwame A.",
      role: "Legal Partner",
    },
  ];

  return (
    <section className="bg-[#f7f4ec] px-6 py-24 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <motion.div {...fadeUp(0)} className="mb-12 text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
            Testimonials
          </p>
          <h2 className="mt-3 text-4xl font-black text-black sm:text-5xl">
            What people are saying
          </h2>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-2">
          {items.map(({ type, quote, avatar, name, role }, i) => (
            <motion.div
              key={name}
              {...fadeUp(i * 0.1)}
              className="p-8 bg-white rounded-3xl border shadow-sm border-black/8"
            >
              <span className="inline-block rounded-full border border-[#FFD221]/40 bg-[#FFD221]/10 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-[#c9a000]">
                {type}
              </span>
              <p className="mt-5 text-base leading-relaxed text-gray-700">
                "{quote}"
              </p>
              <div className="flex gap-3 items-center mt-6">
                <img
                  src={avatar}
                  alt={name}
                  className="object-cover w-10 h-10 rounded-full"
                />
                <div>
                  <p className="text-sm font-black text-black">{name}</p>
                  <p className="text-xs text-gray-500">{role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   14. FAQ
───────────────────────────────────────────── */
const faqs = [
  {
    q: "What is InfoBoxx?",
    a: "InfoBoxx is a platform that helps you understand your options before connecting you to experts. It captures your intent, educates you clearly, and routes you to verified service providers.",
  },
  {
    q: "Is InfoBoxx free to use?",
    a: "Yes, accessing information is free. Some services provided by partners may have costs — those are clearly indicated on each provider's profile.",
  },
  {
    q: "How do I speak to an expert?",
    a: "You can chat via WhatsApp, book an appointment through our booking system, or request a callback — all from within the relevant topic page.",
  },
  {
    q: "Are service providers verified?",
    a: "Yes. All partners go through a verification and approval process before being listed on InfoBoxx. We only work with credible, registered businesses.",
  },
  {
    q: "How does InfoBoxx make money?",
    a: "Through partnerships, sponsorships, and content-based engagement systems. Partners pay to feature content and receive qualified leads.",
  },
  {
    q: "Can I become a partner?",
    a: "Yes. Register your business and go through our simple onboarding process. Once verified and approved, you start receiving qualified leads.",
  },
];

function FAQ() {
  const [open, setOpen] = useState(null);

  return (
    <section className="px-6 py-24 bg-white lg:px-12">
      <div className="mx-auto max-w-4xl">
        <motion.div {...fadeUp(0)} className="mb-12 text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#c9a000]">
            FAQs
          </p>
          <h2 className="mt-3 text-4xl font-black text-black sm:text-5xl">
            Frequently asked questions
          </h2>
        </motion.div>

        <div className="space-y-3">
          {faqs.map(({ q, a }, i) => (
            <motion.div
              key={q}
              {...fadeUp(i * 0.06)}
              className="overflow-hidden rounded-2xl border border-black/8 bg-[#f7f4ec]"
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="flex gap-4 justify-between items-center px-6 py-5 w-full text-left"
              >
                <span className="text-sm font-black text-black">{q}</span>
                <motion.span
                  animate={{ rotate: open === i ? 180 : 0 }}
                  transition={{ duration: 0.25 }}
                  className="shrink-0"
                >
                  <ChevronDown size={18} className="text-[#c9a000]" />
                </motion.span>
              </button>

              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.28, ease: "easeOut" }}
                    className="overflow-hidden"
                  >
                    <p className="px-6 pt-4 pb-5 text-sm leading-relaxed text-gray-600 border-t border-black/8">
                      {a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   15. FINAL CTA
───────────────────────────────────────────── */
function FinalCTA() {
  return (
    <section className="overflow-hidden relative px-6 py-24 text-white bg-gray-950 lg:px-12">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 h-96 w-[60vw] -translate-x-1/2 rounded-full bg-[#FFD221]/10 blur-[100px]" />
        <div className="absolute left-0 top-0 h-px w-full bg-gradient-to-r from-transparent via-[#FFD221]/60 to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl text-center">
        <motion.div {...fadeUp(0)}>
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#FFD221]">
            Get Started
          </p>
          <h2 className="mt-4 text-5xl font-black leading-tight sm:text-6xl">
            Ready to{" "}
            <span
              style={{
                background: "linear-gradient(135deg,#FFD221 0%,#c9a000 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Get Started?
            </span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-gray-400">
            Whether you're here to learn, connect, or grow your business —
            InfoBoxx has a path for you.
          </p>
        </motion.div>

        <motion.div
          {...fadeUp(0.14)}
          className="flex flex-wrap gap-4 justify-center mt-12"
        >
          <Link
            to="/#categories"
            className="inline-flex items-center gap-2 rounded-xl bg-[#FFD221] px-8 py-4 text-sm font-black text-black shadow-lg shadow-[#FFD221]/20 hover:bg-[#e6bc00] transition-colors"
          >
            <Layers size={16} />
            Explore Categories
          </Link>
          <Link
            to="/contact"
            className="inline-flex gap-2 items-center px-8 py-4 text-sm font-black text-white rounded-xl border backdrop-blur transition-colors border-white/20 bg-white/5 hover:bg-white/10"
          >
            <MessageCircle size={16} />
            Ask a Question
          </Link>
          <Link
            to="/partners#how-to-become-a-partner"
            className="inline-flex items-center gap-2 rounded-xl border border-[#FFD221]/40 bg-[#FFD221]/10 px-8 py-4 text-sm font-black text-[#FFD221] hover:bg-[#FFD221]/20 transition-colors"
          >
            <Users size={16} />
            Become a Partner
          </Link>
        </motion.div>

        <motion.p
          {...fadeUp(0.24)}
          className="mt-8 text-xs text-gray-600"
        >
          Free to explore · No account required to browse
        </motion.p>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   PAGE ASSEMBLY
───────────────────────────────────────────── */
const AboutPage = () => {
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <div className="min-h-screen [font-family:'Onest',sans-serif]">
      <Hero />
      <Story />
      <WhoWeAre />
      <WhatWeDo />
      <Objectives />
      <Features />
      <WhyInfoBoxx />
      <Categories />
      <Promise />
      <BecomePartnerSection />
      <Demo />
      <PartnersStrip />
      <Testimonials />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
};

export default AboutPage;
